import { computed, signal } from "../../core/signals/primitives.js";
import { effect } from "../../core/signals/effect.js";
import { createMachineReactor } from "../../core/reactors/create-machine-reactor.js";
import { isResolvedTrack } from "../../media/types/index.js";
import { findTrackById } from "../../media/utils/tracks.js";
import { onMediaSourceReadyStateChange } from "../../media/dom/mse/mediasource-setup.js";
import { getMaxBufferedEnd, waitForSourceBuffersReady } from "../../media/dom/mse/duration.js";
import { isLastSegmentAppended } from "../../media/dom/mse/end-of-stream.js";
//#region src/playback/behaviors/dom/end-of-stream.ts
/**
* Slack (seconds) on the "playhead has reached the last segment" gate. A tiny final segment (e.g. Apple's ~44ms last
* segment) starts right at the buffered end, and the browser freezes the playhead ~50–70ms short of that end (its
* render horizon), so a strict `currentTime >= lastSegStart` would never open — deadlocking `endOfStream` (the
* MediaSource stays `'open'`, so the browser keeps the playhead frozen waiting for data/EOS that never comes). This
* slack lets a playhead stalled just short of the final segment still finalize. Firing slightly early is harmless: the
* last segment is already appended (the gate above), so no more data is expected.
*/
const LAST_SEGMENT_REACHED_SLACK = .5;
function deriveState(presentation, mediaSource, msIsOpen, videoBufferActor, audioBufferActor, currentTime) {
	if (!mediaSource || !presentation || !msIsOpen) return "preconditions-unmet";
	const actors = [videoBufferActor, audioBufferActor].filter((a) => a !== void 0);
	if (actors.length === 0) return "preconditions-unmet";
	let lastSegStart;
	for (const actor of actors) {
		const snapshot = actor.snapshot.get();
		if (snapshot.value !== "idle") return "preconditions-unmet";
		const { initTrackId, segments: appended } = snapshot.context;
		if (!initTrackId) return "preconditions-unmet";
		const track = findTrackById(presentation, initTrackId);
		if (!track || !isResolvedTrack(track)) return "preconditions-unmet";
		if (!Number.isFinite(track.duration)) return "preconditions-unmet";
		if (!isLastSegmentAppended(track.segments, appended)) return "preconditions-unmet";
		if (track.segments.length > 0) {
			const start = track.segments[track.segments.length - 1].startTime;
			if (lastSegStart === void 0 || start > lastSegStart) lastSegStart = start;
		}
	}
	if (lastSegStart !== void 0 && (currentTime ?? 0) < lastSegStart - LAST_SEGMENT_REACHED_SLACK) return "preconditions-unmet";
	return "eos-ready";
}
function endOfStreamSetup({ state, context }) {
	const msIsOpen = signal(false);
	const cleanupMsListener = effect(() => {
		const mediaSource = context.mediaSource.get();
		if (!mediaSource) {
			msIsOpen.set(false);
			return;
		}
		msIsOpen.set(mediaSource.readyState === "open");
		const controller = new AbortController();
		onMediaSourceReadyStateChange(mediaSource, controller.signal, (rs) => {
			msIsOpen.set(rs === "open");
		});
		return () => controller.abort();
	});
	const derivedStateSignal = computed(() => deriveState(state.presentation.get(), context.mediaSource.get(), msIsOpen.get(), context.videoBufferActor?.get(), context.audioBufferActor?.get(), state.currentTime.get()));
	const reactor = createMachineReactor({
		initial: "preconditions-unmet",
		monitor: () => derivedStateSignal.get(),
		states: {
			"preconditions-unmet": {},
			"eos-ready": { entry: () => {
				const mediaSource = context.mediaSource.get();
				const controller = new AbortController();
				const endStreamWhenReady = async () => {
					await waitForSourceBuffersReady(mediaSource.sourceBuffers, controller.signal);
					if (controller.signal.aborted) return;
					const bufferedEnd = getMaxBufferedEnd(mediaSource.sourceBuffers);
					if (bufferedEnd > 0) mediaSource.duration = bufferedEnd;
					mediaSource.endOfStream();
				};
				endStreamWhenReady().catch((err) => console.error("Failed to call endOfStream:", err));
				return () => controller.abort();
			} }
		}
	});
	return () => {
		cleanupMsListener();
		reactor.destroy();
	};
}
/**
* `endOfStream` uses a manual `Behavior<>` literal (rather than `defineBehavior`) because it reads `videoBufferActor` /
* `audioBufferActor` defensively without declaring them in its contextKeys — those slots are contributed by other
* behaviors and compose conditionally per engine variant. The `Behavior<>` literal opts out of the exhaustiveness check
* so the typed context shape can include the optional fields used at runtime. See the comment on `endOfStreamSetup`'s
* context param for the discipline.
*/
const endOfStream = {
	stateKeys: ["presentation", "currentTime"],
	contextKeys: ["mediaSource"],
	setup: endOfStreamSetup
};
//#endregion
export { endOfStream };

//# sourceMappingURL=end-of-stream.js.map